/**
 * Standard ad sizes and placement slot recipes.
 * Slots are programmatic placeholders; the ads table fills them at runtime.
 */

export const AD_SIZES = {
    '300x250': { width: 300, height: 250, label: 'Medium Rectangle' },
    '300x600': { width: 300, height: 600, label: 'Half Page' },
    '320x50': { width: 320, height: 50, label: 'Mobile Banner' },
    '320x100': { width: 320, height: 100, label: 'Large Mobile Banner' },
    '728x90': { width: 728, height: 90, label: 'Leaderboard' },
    /** Fits the homepage main column (~max-w-7xl minus 300px sidebar). */
    '970x90': { width: 970, height: 90, label: 'Large Leaderboard' },
} as const;

export type AdSizeKey = keyof typeof AD_SIZES;

/** Slot size may accept a smaller compatible creative (e.g. 728 into 970). */
const AD_SIZE_FALLBACKS: Partial<Record<AdSizeKey, readonly AdSizeKey[]>> = {
    '970x90': ['728x90'],
};

export const AD_PLACEMENTS = {
    'homepage-sidebar': { label: 'סרגל בית' },
    'homepage-leaderboard': { label: 'באנר בית' },
    'article-inline': { label: 'בתוך כתבה' },
    'article-sidebar': { label: 'סרגל כתבה' },
    'category-inline': { label: 'בתוך מדור' },
} as const;

export type AdPlacementKey = keyof typeof AD_PLACEMENTS;

/** Default primary CTA color (matches `--brand-accent`). */
export const DEFAULT_AD_CTA_COLOR = '#e24b3d';

/** Preset CTA colors for the ads console. */
export const AD_CTA_COLOR_PRESETS = [
    { label: 'מותג', value: DEFAULT_AD_CTA_COLOR },
    { label: 'כחול', value: '#3b82f6' },
    { label: 'ירוק', value: '#22c55e' },
    { label: 'כתום', value: '#f97316' },
    { label: 'סגול', value: '#a855f7' },
    { label: 'ציאן', value: '#06b6d4' },
    { label: 'זהב', value: '#eab308' },
    { label: 'לבן', value: '#ffffff' },
] as const;

/**
 * Normalize a hex color to lowercase `#rrggbb`, or null if invalid / empty.
 * Accepts `#rgb` and `#rrggbb`.
 */
export function normalizeAdCtaColor(
    input: string | null | undefined,
): string | null {
    if (!input) return null;
    const raw = input.trim();
    const short = /^#([0-9a-fA-F]{3})$/.exec(raw);
    if (short) {
        const [r, g, b] = short[1]!;
        return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
    }
    const full = /^#([0-9a-fA-F]{6})$/.exec(raw);
    if (full) return `#${full[1]}`.toLowerCase();
    return null;
}

/** Resolved CTA color for rendering (falls back to brand accent). */
export function resolveAdCtaColor(
    input: string | null | undefined,
): string {
    return normalizeAdCtaColor(input) ?? DEFAULT_AD_CTA_COLOR;
}

/** Resolved CTA text color (explicit hex or auto contrast against the background). */
export function resolveAdCtaTextColor(
    textColor: string | null | undefined,
    backgroundColor: string | null | undefined,
): string {
    return (
        normalizeAdCtaColor(textColor) ??
        adCtaForeground(resolveAdCtaColor(backgroundColor))
    );
}

function srgbChannelToLinear(channel: number): number {
    const s = channel / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance(hex: string): number {
    const n = Number.parseInt(hex.slice(1), 16);
    const r = srgbChannelToLinear((n >> 16) & 255);
    const g = srgbChannelToLinear((n >> 8) & 255);
    const b = srgbChannelToLinear(n & 255);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrastRatio(l1: number, l2: number): number {
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
}

/** Text color for a solid CTA fill - whichever of black/white has higher WCAG contrast. */
export function adCtaForeground(hex: string): '#0a0a0a' | '#ffffff' {
    const normalized = normalizeAdCtaColor(hex) ?? DEFAULT_AD_CTA_COLOR;
    const bg = relativeLuminance(normalized);
    const black = contrastRatio(bg, relativeLuminance('#0a0a0a'));
    const white = contrastRatio(bg, relativeLuminance('#ffffff'));
    return black >= white ? '#0a0a0a' : '#ffffff';
}

export interface AdSlotDef {
    /** Stable slot id within a placement layout. */
    id: string;
    size: AdSizeKey;
}

/** Desktop homepage sidebar: 300px column, IAB medium / half-page units. */
export const HOMEPAGE_SIDEBAR_SLOTS: AdSlotDef[] = [
    { id: 'top', size: '300x600' },
    { id: 'mid', size: '300x250' },
    { id: 'tall', size: '300x600' },
    { id: 'bottom', size: '300x250' },
];

/** Mobile compact rail under the feed. */
export const HOMEPAGE_SIDEBAR_COMPACT_SLOTS: AdSlotDef[] = [
    { id: 'compact', size: '320x50' },
    { id: 'compact-mid', size: '300x250' },
];

/** Full-width leaderboard above / between homepage feed sections (desktop). */
export const HOMEPAGE_LEADERBOARD_SLOTS: AdSlotDef[] = [
    { id: 'leaderboard', size: '970x90' },
];

/** Mid-article medium rectangle. */
export const ARTICLE_INLINE_SLOTS: AdSlotDef[] = [
    { id: 'mid', size: '300x250' },
];

/** Article sticky rail (desktop). */
export const ARTICLE_SIDEBAR_SLOTS: AdSlotDef[] = [
    { id: 'top', size: '300x250' },
    { id: 'tall', size: '300x600' },
];

/** Category listing inline unit. */
export const CATEGORY_INLINE_SLOTS: AdSlotDef[] = [
    { id: 'mid', size: '300x250' },
];

export function adSizeStyle(size: AdSizeKey): { width: number; height: number; aspectRatio: string } {
    const { width, height } = AD_SIZES[size];
    return { width, height, aspectRatio: `${width} / ${height}` };
}

export interface FilledAdSlot<T extends { size: AdSizeKey }> {
    slot: AdSlotDef;
    ad?: T;
}

/** Fisher-Yates shuffle (in place). Server-side rotation per request. */
function shuffleInPlace<T>(items: T[]): T[] {
    for (let i = items.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        const tmp = items[i]!;
        items[i] = items[j]!;
        items[j] = tmp;
    }
    return items;
}

/**
 * Assign active ads to placement slots by matching size.
 * Shuffles the pool so each request rotates which creative fills each slot;
 * each ad is used at most once on the page.
 */
function adFitsSlot(adSize: AdSizeKey, slotSize: AdSizeKey): boolean {
    if (adSize === slotSize) return true;
    return AD_SIZE_FALLBACKS[slotSize]?.includes(adSize) ?? false;
}

export function fillAdSlots<T extends { size: AdSizeKey }>(
    slots: readonly AdSlotDef[],
    ads: readonly T[],
): FilledAdSlot<T>[] {
    const pool = shuffleInPlace([...ads]);
    return slots.map((slot) => {
        const index = pool.findIndex((ad) => adFitsSlot(ad.size, slot.size));
        if (index < 0) return { slot };
        const [ad] = pool.splice(index, 1);
        return { slot, ad };
    });
}

const AD_UTM_SOURCE = 'ashkelon.news';
const AD_UTM_MEDIUM = 'display';

function hasUtmParams(searchParams: URLSearchParams): boolean {
    for (const key of searchParams.keys()) {
        if (key.toLowerCase().startsWith('utm_')) return true;
    }
    return false;
}

/** URL-safe campaign token from an ad name, falling back to the ad id. */
export function adUtmCampaign(ad: { $id: string; name: string }): string {
    const slug = ad.name
        .trim()
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 64);
    return slug || ad.$id;
}

/**
 * Append standard ad UTM params when the destination has none yet.
 * Leaves the URL unchanged if any `utm_*` query param is already present.
 */
export function withAdUtm(
    url: string,
    opts?: { campaign?: string; content?: string },
): string {
    const trimmed = url.trim();
    if (!trimmed || trimmed === '#') return url;

    const campaign = (opts?.campaign?.trim() || 'ad').slice(0, 64);
    const content = opts?.content?.trim().slice(0, 64);

    try {
        const parsed = new URL(trimmed);
        if (hasUtmParams(parsed.searchParams)) return trimmed;
        parsed.searchParams.set('utm_source', AD_UTM_SOURCE);
        parsed.searchParams.set('utm_medium', AD_UTM_MEDIUM);
        parsed.searchParams.set('utm_campaign', campaign);
        if (content) parsed.searchParams.set('utm_content', content);
        return parsed.toString();
    } catch {
        if (/[?&]utm_/i.test(trimmed)) return trimmed;
        const sep = trimmed.includes('?') ? '&' : '?';
        let out = `${trimmed}${sep}utm_source=${encodeURIComponent(AD_UTM_SOURCE)}&utm_medium=${encodeURIComponent(AD_UTM_MEDIUM)}&utm_campaign=${encodeURIComponent(campaign)}`;
        if (content) {
            out += `&utm_content=${encodeURIComponent(content)}`;
        }
        return out;
    }
}
